import { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ImageUploader from '../input/image-uploader';
import { extractRecipeFromImage, extractRecipeFromText } from '../../services/gemini-service';
import type { ExtractedRecipe } from '../../types/recipe-import';

type ImportMode = 'select' | 'image' | 'text';

interface ImportRecipeDialogProps {
    open: boolean;
    handleClose: () => void;
    onImportComplete: (extractedRecipe: ExtractedRecipe) => void;
}

export default function ImportRecipeDialog(props: ImportRecipeDialogProps) {
    const [mode, setMode] = useState<ImportMode>('select');
    const [imageData, setImageData] = useState<string>('');
    const [textInput, setTextInput] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');

    const handleClose = () => {
        // Reset state
        setMode('select');
        setImageData('');
        setTextInput('');
        setLoading(false);
        setError('');
        props.handleClose();
    };

    const handleImageUpload = (imgData: any) => {
        setImageData(imgData);
        setError('');
    };

    const handleImageError = (err: any) => {
        setError('Image upload failed. Please try again.');
        console.error('Image upload error:', err);
    };

    const handleImportFromImage = async () => {
        if (!imageData) return;

        setLoading(true);
        setError('');

        try {
            const result = await extractRecipeFromImage(imageData);

            if (result.success && result.recipe) {
                props.onImportComplete(result.recipe);
                handleClose();
            } else {
                setError(result.error || 'Failed to extract recipe from image.');
            }
        } catch (err: any) {
            setError(err.message || 'An unexpected error occurred.');
        } finally {
            setLoading(false);
        }
    };

    const handleImportFromText = async () => {
        if (!textInput.trim()) return;

        setLoading(true);
        setError('');

        try {
            const result = await extractRecipeFromText(textInput);

            if (result.success && result.recipe) {
                props.onImportComplete(result.recipe);
                handleClose();
            } else {
                setError(result.error || 'Failed to extract recipe from text.');
            }
        } catch (err: any) {
            setError(err.message || 'An unexpected error occurred.');
        } finally {
            setLoading(false);
        }
    };

    const renderModeSelection = () => (
        <>
            <DialogTitle>Import Recipe</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Choose how you'd like to import your recipe:
                </DialogContentText>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Button
                        variant="outlined"
                        size="large"
                        startIcon={<CameraAltIcon />}
                        onClick={() => setMode('image')}
                    >
                        Upload Image
                    </Button>
                    <Button
                        variant="outlined"
                        size="large"
                        startIcon={<TextFieldsIcon />}
                        onClick={() => setMode('text')}
                    >
                        Paste Text or URL
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
            </DialogActions>
        </>
    );

    const renderImageMode = () => (
        <>
            <DialogTitle>Import from Image</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Upload a photo of a recipe to extract ingredients and instructions.
                </DialogContentText>
                <Box sx={{ mt: 2, mb: 2 }}>
                    <ImageUploader
                        onChange={handleImageUpload}
                        onError={handleImageError}
                        buttonText={imageData ? "Change Image" : "Upload Image"}
                    />
                </Box>
                {imageData && (
                    <Box sx={{ mt: 2 }}>
                        <img
                            src={imageData}
                            alt="Recipe preview"
                            style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                        />
                    </Box>
                )}
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
                {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                        <CircularProgress size={24} />
                        <span>Extracting recipe...</span>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => {
                        setMode('select');
                        setImageData('');
                        setError('');
                    }}
                    disabled={loading}
                >
                    Back
                </Button>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleImportFromImage}
                    variant="contained"
                    color="primary"
                    disabled={!imageData || loading}
                >
                    Import
                </Button>
            </DialogActions>
        </>
    );

    const renderTextMode = () => (
        <>
            <DialogTitle>Import from Text or URL</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Paste a recipe URL (e.g., from a recipe website) or the full recipe text including ingredients and instructions.
                </DialogContentText>
                <TextField
                    autoFocus
                    margin="dense"
                    fullWidth
                    multiline
                    rows={8}
                    placeholder="Paste recipe text or URL here..."
                    value={textInput}
                    onChange={(e) => {
                        setTextInput(e.target.value);
                        setError('');
                    }}
                    sx={{ mt: 2 }}
                />
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
                {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                        <CircularProgress size={24} />
                        <span>Extracting recipe...</span>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => {
                        setMode('select');
                        setTextInput('');
                        setError('');
                    }}
                    disabled={loading}
                >
                    Back
                </Button>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleImportFromText}
                    variant="contained"
                    color="primary"
                    disabled={!textInput.trim() || loading}
                >
                    Import
                </Button>
            </DialogActions>
        </>
    );

    return (
        <Dialog open={props.open} onClose={handleClose} maxWidth="sm" fullWidth>
            {mode === 'select' && renderModeSelection()}
            {mode === 'image' && renderImageMode()}
            {mode === 'text' && renderTextMode()}
        </Dialog>
    );
}
